
import { Link } from 'react-router-dom';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger 
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Menu,
  Store,
  BookOpen,
  Map,
  UserCircle,
  MessageSquare,
  LogOut,
  LogIn,
  Info,
  Tags,
  Mail,
  BookOpenCheck,
  Crown,
  LayoutDashboard
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/use-subscription';
import { useState } from 'react';
import { SITE_IMAGES } from '@/config/images';

interface MobileMenuProps {
  isLoggedIn: boolean;
  onLogout: () => Promise<void>;
  onLogin: () => Promise<void>;
}

export const MobileMenu = ({ isLoggedIn, onLogout, onLogin }: MobileMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { hasActiveSubscription } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = async () => {
    setIsOpen(false); // Close the mobile menu first
    await onLogout();
  };

  const handleLogin = async () => {
    setIsOpen(false); // Close the mobile menu first
    await onLogin();
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild className="block md:hidden">
        <Button variant="ghost" size="icon" className="text-white hover:text-unimog-200 hover:bg-unimog-700">
          <Menu size={24} />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-background border-r">
        <nav className="flex flex-col gap-4 mt-8">
          <Link to="/" className="text-lg font-bold flex items-center gap-2 text-unimog-800 dark:text-unimog-200">
            <img src={SITE_IMAGES.logo} alt="Unimog Hub Logo" className="w-14 h-14 rounded-full" />
            Unimog Hub
          </Link>

          {isLoggedIn ? (
            <>
              <Link to="/dashboard" className="nav-link flex items-center gap-2">
                <LayoutDashboard size={18} />
                Dashboard
              </Link>
              <Link to="/marketplace" className="nav-link flex items-center gap-2">
                <Store size={18} />
                Marketplace
              </Link>
              <Link to="/knowledge" className="nav-link flex items-center gap-2">
                <BookOpen size={18} />
                Knowledge Base
              </Link>
              <Link to="/trips" className="nav-link flex items-center gap-2">
                <Map size={18} />
                Trip Planning
              </Link>
              <Link to="/community" className="nav-link flex items-center gap-2">
                <UserCircle size={18} />
                Community
              </Link>
              <Link to="/messages" className="nav-link flex items-center gap-2">
                <MessageSquare size={18} />
                Messages
              </Link>

              {/* Learn About Unimogs - moved to bottom, less prominent */}
              <Link to="/learn-about-unimogs" className="nav-link flex items-center gap-2 mt-4 text-sm opacity-75">
                <BookOpenCheck size={16} />
                Learn About Unimogs
                {!hasActiveSubscription() && <Crown size={12} className="ml-auto text-amber-500" />}
              </Link>

              <button
                onClick={handleLogout}
                className="nav-link flex items-center gap-2 mt-2 text-red-500"
              >
                <LogOut size={18} />
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/about" className="nav-link flex items-center gap-2">
                <Info size={18} />
                About
              </Link>
              <Link to="/pricing" className="nav-link flex items-center gap-2">
                <Tags size={18} />
                Pricing
              </Link>
              <Link to="/contact" className="nav-link flex items-center gap-2">
                <Mail size={18} />
                Contact
              </Link>
              <button 
                onClick={handleLogin}
                className="nav-link flex items-center gap-2 mt-4"
              >
                <LogIn size={18} />
                Log In
              </button>
            </>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
};
